import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  CreditCard, 
  Save, 
  ArrowLeft,
  Compass, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle, 
  Shield,
  Lock,
  Banknote,
  Star,
  Calendar,
  Building,
  User,
  Mail,
  Hotel,
  ShoppingCart ,
  DollarSign,
  LogOut,
  X,
  AlertTriangle,
  Smartphone
} from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import MainLayout from '../components/Layout/MainLayout'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'
import AddStripeCardModal from '../components/AddStripeCardModal'
import { Elements } from '@stripe/react-stripe-js'
import { stripePromise } from '../config/stripe'
import  { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCheck, FaTimes  } from 'react-icons/fa';
import experienceCheckoutService from '../services/experienceCheckoutService';

const BankAccountPage = () => {
  const [bankAccount, setBankAccount] = useState(null)
  const [formData, setFormData] = useState({
    accountHolderName: '',
    accountNumber: '',
    iban: '',
    routingNumber: '',
    swiftBicCode: '',
    bankName: '',
    accountType: 'checking',
    currencyPreference: 'USD'
  })
  const [verificationCode, setVerificationCode] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [showAccountNumber, setShowAccountNumber] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [isEditing, setIsEditing] = useState(false)
  const navigate = useNavigate()
  const [mounted, setMounted] = useState(false)
  const location = useLocation()
  const cardBrandIcons = {
    visa: <FaCcVisa className="h-6 w-6" />,
    mastercard: <FaCcMastercard className="h-6 w-6" />,
    amex: <FaCcAmex className="h-6 w-6" />,
    discover: <FaCcDiscover className="h-6 w-6" />,
    default: <CreditCard className="h-6 w-6" />,
  };
  const [isLoadingCardSection, setIsLoadingCardSection] = useState(true)

  // Stripe cards for experience checkout
  const [stripePaymentMethods, setStripePaymentMethods] = useState([])
  const [stripeSetupClientSecret, setStripeSetupClientSecret] = useState('')
  const [stripeSetupCustomerSessionSecret, setStripeSetupCustomerSessionSecret] = useState('')
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false)
  const [isLoadingStripeCards, setIsLoadingStripeCards] = useState(true)
  const [stripeDeleteModalOpen, setStripeDeleteModalOpen] = useState(false)
  const [stripePaymentToDelete, setStripePaymentToDelete] = useState(null)



  useEffect(() => {
    loadBankAccount()
    
    // Check for Stripe SetupIntent result in URL (after redirect)
    const params = new URLSearchParams(window.location.search);
    const setupIntentId = params.get('setup_intent');
    const setupStatus = params.get('setup_intent_status');

    if (setupIntentId && setupStatus === 'succeeded') {
      const finishSetup = async () => {
        try {
          // Retrieve the setup intent to get the payment method ID
          const stripe = await stripePromise;
          const { setupIntent } = await stripe.retrieveSetupIntent(params.get('setup_intent_client_secret'));
          
          if (setupIntent?.payment_method) {
            await handleSaveStripeCard(setupIntent.payment_method);
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error('Error finishing setup:', err);
        }
      };
      finishSetup();
    }
  }, [])

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleLogout = () => {
    travelAgentService.logout()
    navigate('/login')
  }

  const loadBankAccount = async () => {
    try {
      const response = await travelAgentService.getBankAccount()
      
      setBankAccount(response.data)

      // Reset editing state when loading
      setIsEditing(false)
      
      if (response.data && response.data.accountNumber) {
        setFormData({
          accountHolderName: response.data.accountHolderName || '',
          accountNumber: '', // Don't populate account number for security - user must re-enter
          iban: response.data.iban || '', // Populate IBAN to send in updates
          routingNumber: response.data.routingNumber || '',
          swiftBicCode: response.data.swiftBicCode || '', // Populate SWIFT/BIC to send in updates
          bankName: response.data.bankName || '',
          accountType: response.data.accountType || 'checking',
          currencyPreference: response.data.currencyPreference || 'USD'
        })
      }
    } catch (error) {
      console.error('Failed to load bank account:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load bank account information' })
    } finally {
      setIsLoading(false)
    }
  }

  // Check if travel agent is approved
  const checkApprovalStatus = async () => {
    try {
      const profileResponse = await travelAgentService.getProfile()
      
      if (profileResponse.data.applicationStatus !== 'approved') {
        setMessage({ 
          type: 'error', 
          text: 'Your application must be approved before you can add a bank account. Please wait for admin approval.' 
        })
        return false
      }
      return true
    } catch (error) {
      console.error('Failed to check approval status:', error)
      return false
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage({ type: '', text: '' })

    try {
      
      
      
      // Check approval status first
      const isApproved = await checkApprovalStatus()
      if (!isApproved) {
        setIsSaving(false)
        return
      }
      
      // Prepare data for submission - ensure account number has no spaces
      const submissionData = {
        ...formData,
        accountNumber: formData.accountNumber.replace(/\s/g, ''), // Remove all spaces
        routingNumber: formData.routingNumber.replace(/\s/g, '')  // Remove all spaces
      }
      
      
      
      // Client-side validation
      if (!submissionData.accountHolderName || submissionData.accountHolderName.length < 2 || submissionData.accountHolderName.length > 100) {
        setMessage({ type: 'error', text: 'Account holder name must be between 2 and 100 characters' })
        setIsSaving(false)
        return
      }
      
      // if (!submissionData.accountNumber || !/^\d{8,17}$/.test(submissionData.accountNumber)) {
      //   setMessage({ type: 'error', text: 'Account number must be between 8 and 17 digits' })
      //   setIsSaving(false)
      //   return
      // }
      
      if (submissionData.routingNumber && !/^\d{9}$/.test(submissionData.routingNumber)) {
        setMessage({ type: 'error', text: 'Routing number must be exactly 9 digits' })
        setIsSaving(false)
        return
      }
      
      if (!submissionData.bankName || submissionData.bankName.length < 2 || submissionData.bankName.length > 100) {
        setMessage({ type: 'error', text: 'Bank name must be between 2 and 100 characters' })
        setIsSaving(false)
        return
      }
      
      if (!['checking', 'savings', 'business'].includes(submissionData.accountType)) {
        setMessage({ type: 'error', text: 'Invalid account type' })
        setIsSaving(false)
        return
      }
      
      // Check if we have an existing bank account with actual data
      const hasExistingAccount = bankAccount && bankAccount.accountHolderName && bankAccount.bankName
      
      
      // If we have an existing account AND we're in editing mode, update it
      if (hasExistingAccount && isEditing) {
        // Update existing account
        
        await travelAgentService.updateBankAccount(submissionData)
        setMessage({ type: 'success', text: 'Bank account updated successfully. Please verify your account.' })
      } else {
        // Add new account (either no existing account or not in editing mode)
        
        await travelAgentService.addBankAccount(submissionData)
        setMessage({ type: 'success', text: 'Bank account added successfully. Please verify your account.' })
      }
      
      setIsEditing(false)
      await loadBankAccount()
    } catch (error) {
      console.error('Failed to save bank account:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to save bank account' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    setIsVerifying(true)
    setMessage({ type: '', text: '' })

    try {
      await travelAgentService.verifyBankAccount(verificationCode)
      setMessage({ type: 'success', text: 'Bank account verified successfully!' })
      setVerificationCode('')
      await loadBankAccount()
    } catch (error) {
      console.error('Failed to verify bank account:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to verify bank account' })
    } finally {
      setIsVerifying(false)
    }
  }

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove your bank account? This action cannot be undone.')) {
      return
    }

    setIsSaving(true)
    setMessage({ type: '', text: '' })

    try {
      await travelAgentService.removeBankAccount()
      setMessage({ type: 'success', text: 'Bank account removed successfully' })
      setBankAccount(null)
      setFormData({
        accountHolderName: '',
        accountNumber: '',
        iban: '',
        routingNumber: '',
        swiftBicCode: '',
        bankName: '',
        accountType: 'checking',
        currencyPreference: 'USD'
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to remove bank account:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to remove bank account' })
    } finally {
      setIsSaving(false)
    }
  }

  const formatAccountNumber = (number) => {
    if (!number) return ''
    return number.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim()
  }

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '')
    setFormData(prev => ({
      ...prev,
      accountNumber: value
    }))
  }

  const handleRoutingNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 9)
    setFormData(prev => ({
      ...prev,
      routingNumber: value
    }))
  }

  useEffect(() => {
    loadStripePaymentMethods()
  }, [])

  const loadStripePaymentMethods = async () => {
    setIsLoadingCardSection(true)
    setIsLoadingStripeCards(true)
    try {
      const res = await experienceCheckoutService.getStripePaymentMethods()
      setStripePaymentMethods(res.paymentMethods || [])
    } catch (err) {
      console.error('Load Stripe payment methods:', err)
      setMessage({ type: 'error', text: err.message || 'Failed to load Stripe cards' })
    } finally {
      setIsLoadingCardSection(false)
      setIsLoadingStripeCards(false)
    }
  }

  const handleRequestStripeSetupIntent = async () => {
    if (stripeSetupClientSecret) return
    try {
      const res = await experienceCheckoutService.createSetupIntent()
      setStripeSetupClientSecret(res.clientSecret || '')
      setStripeSetupCustomerSessionSecret(res.customerSessionClientSecret || '')
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to start add card' })
    }
  }

  const handleSaveStripeCard = async (paymentMethodId) => {
    try {
      await experienceCheckoutService.saveStripePaymentMethod(paymentMethodId)
      setMessage({ type: 'success', text: 'Card saved successfully' })
      setStripeSetupClientSecret('')
      setIsStripeModalOpen(false)
      await loadStripePaymentMethods()
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save card' })
      throw err
    }
  }

  const handleDeleteStripePaymentMethod = (id) => {
    setStripePaymentToDelete(id)
    setStripeDeleteModalOpen(true)
  }

  const confirmDeleteStripePaymentMethod = async () => {
    if (!stripePaymentToDelete) return
    try {
      await experienceCheckoutService.removeStripePaymentMethod(stripePaymentToDelete)
      setMessage({ type: 'success', text: 'Card removed' })
      setStripePaymentToDelete(null)
      setStripeDeleteModalOpen(false)
      await loadStripePaymentMethods()
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to remove card' })
    } finally {
      setStripePaymentToDelete(null)
      setStripeDeleteModalOpen(false)
    }
  }

  // Note: Hotel + Experience use the same saved Stripe cards now (single source of truth).

  if (isLoading) {
    return (
      <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50/50 overflow-x-hidden">
        {/* Hero */}
        <section className="relative min-h-[20rem] sm:h-96 bg-black overflow-hidden">
          <div className="h-12 shrink-0" aria-hidden />
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
            style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30" />
          <div className="relative z-10 flex items-center min-h-[11rem] sm:h-[calc(24rem-3rem)] px-4 sm:px-6 lg:px-8 py-6 sm:py-0">
            <div className="max-w-7xl mx-auto w-full min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse shrink-0"></div>
                <p className="text-sm text-slate-300 font-medium tracking-wider uppercase truncate">Financial Settings</p>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl typography-hero-title-on-dark font-bold text-white mb-3 break-words">Payment Methods</h2>
              <p className="text-slate-300 text-base sm:text-lg max-w-2xl mb-6 break-words">
                Securely manage your bank accounts and payment methods for commissions and bookings
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mt-8 sm:mt-10 pb-12 relative z-20 w-full min-w-0">

      <main className="max-w-4xl mx-auto py-6 sm:py-8 px-2 sm:px-6 lg:px-8 w-full min-w-0">

        {/* Security Notice */}
        <div className={`mb-8 bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-4 sm:p-6 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transform transition-all duration-700 delay-100`}>
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Secure Bank Account Management</h3>
              <p className="text-slate-700 leading-relaxed break-words">
                Your bank account information is encrypted and stored securely using industry-standard security measures. 
                We use bank-level encryption to protect your financial data and ensure your information remains confidential.
              </p>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-8 p-4 sm:p-6 rounded-2xl ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200 text-slate-800' 
              : 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                message.type === 'success' ? 'bg-slate-200' : 'bg-red-100'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-slate-700" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <span className="font-medium break-words min-w-0">{message.text}</span>
            </div>
          </div>
        )}

        {/* Bank Account Information */}
        {bankAccount && !isEditing && (
          <div className={`bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-8 mb-8 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transform transition-all duration-700 delay-150`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center shrink-0">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl typography-section-title font-bold text-slate-900 break-words">Current Bank Account</h2>
                  <p className="text-slate-600 text-sm sm:text-base">Your registered payment method</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3 sm:justify-end shrink-0">
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 sm:flex-none min-w-[6rem] px-4 sm:px-6 py-3 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Edit
                </button>
                <button
                  onClick={handleRemove}
                  className="flex-1 sm:flex-none min-w-[6rem] px-4 sm:px-6 py-3 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <User className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Account Holder</label>
                </div>
                <p className="text-slate-900 font-semibold break-words">{bankAccount.accountHolderName}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Building className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bank Name</label>
                </div>
                <p className="text-slate-900 font-semibold break-words">{bankAccount.bankName}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <CreditCard className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Account Number</label>
                </div>
                <p className="text-slate-900 font-semibold break-all">{bankAccount.accountNumber}</p>
              </div>
              {bankAccount.iban && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="h-4 w-4 text-slate-500" />
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">IBAN</label>
                  </div>
                  <p className="text-slate-900 font-semibold break-all">{bankAccount.iban}</p>
                </div>
              )}
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Banknote className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Routing Number</label>
                </div>
                <p className="text-slate-900 font-semibold break-all">{bankAccount.routingNumber}</p>
              </div>
              {bankAccount.swiftBicCode && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <Building className="h-4 w-4 text-slate-500" />
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">SWIFT/BIC Code</label>
                  </div>
                  <p className="text-slate-900 font-semibold break-all">{bankAccount.swiftBicCode}</p>
                </div>
              )}
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <CreditCard className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Account Type</label>
                </div>
                <p className="text-slate-900 font-semibold capitalize">{bankAccount.accountType}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Banknote className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Currency</label>
                </div>
                <p className="text-slate-900 font-semibold">{bankAccount.currencyPreference || 'USD'}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="h-4 w-4 text-slate-500" />
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Verification Status</label>
                </div>
                <div className="flex items-center space-x-2">
                  {bankAccount.isVerified ? (
                    <>
                      <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-slate-700" />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">Verified</span>
                    </>
                  ) : (
                    <>
                      <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                        <AlertCircle className="h-4 w-4 text-slate-600" />
                      </div>
                      <span className="text-sm font-semibold text-slate-700">Pending Verification</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {bankAccount.verificationDate && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Verified On</label>
                  </div>
                  <p className="text-slate-900 font-semibold">
                    {new Date(bankAccount.verificationDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bank Account Form */}
        {(!bankAccount || isEditing) && (
          <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-8 mb-8">
            <div className="flex items-start gap-3 mb-6 min-w-0">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center shrink-0">
                <Banknote className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl typography-section-title font-bold text-slate-900 break-words">
                  {bankAccount && bankAccount.accountHolderName && isEditing ? 'Update Bank Account' : 'Add Bank Account'}
                </h2>
                <p className="text-slate-600">Enter your bank account details securely</p>
              </div>
            </div>
            
            {isEditing && (
              <div className="mb-8 bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                  <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Security Notice</h3>
                    <p className="text-slate-700 leading-relaxed break-words">
                      For your security, the account number field has been cleared. Please re-enter your full account number to update your bank account information.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 min-w-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 [&>div]:min-w-0">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter account holder name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Account Number *
                  </label>
                  <div className="relative">
                    <input
                      type={showAccountNumber ? "text" : "password"}
                      name="accountNumber"
                      // value={formatAccountNumber(formData.accountNumber)}
                      value={formData.accountNumber}
                      onChange={handleAccountNumberChange}
                      required
                      className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                      placeholder={isEditing ? "Re-enter account number for security" : "Enter account number"}
                      maxLength="23"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showAccountNumber ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {isEditing 
                      ? "For security reasons, please re-enter your full account number" 
                      : ""
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    IBAN <span className="text-slate-500 font-normal">(Optional - for international)</span>
                  </label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter IBAN"
                    maxLength="34"
                  />
                  <p className="text-xs text-slate-500 mt-2">International Bank Account Number</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Routing Number <span className="text-slate-500 font-normal">(Optional)</span>

                  </label>
                  <input
                    type="text"
                    name="routingNumber"
                    value={formData.routingNumber}
                    onChange={handleRoutingNumberChange}
                    // required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter 9-digit routing number"
                    maxLength="9"
                  />
                  <p className="text-xs text-slate-500 mt-2">9 digits only</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    SWIFT/BIC Code <span className="text-slate-500 font-normal">(Optional - for international transfers)</span>
                  </label>
                  <input
                    type="text"
                    name="swiftBicCode"
                    value={formData.swiftBicCode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter SWIFT/BIC code"
                    maxLength="11"
                  />
                  <p className="text-xs text-slate-500 mt-2">8 or 11 characters for international transfers</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Account Type *
                  </label>
                  <select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="business">Business</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Currency Preference *
                  </label>
                  <select
                    name="currencyPreference"
                    value={formData.currencyPreference}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-2">Select your preferred currency for commission payments</p>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4 pt-8 border-t border-slate-200">
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-all duration-200"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn-primary w-full sm:w-auto justify-center flex items-center px-8 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900"
                >
                  {isSaving ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {bankAccount && bankAccount.accountHolderName && isEditing ? 'Update Account' : 'Add Account'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Verification Form */}
        {bankAccount && !bankAccount.isVerified && (
          <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-8">
            <div className="flex items-start gap-3 mb-6 min-w-0">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl typography-section-title font-bold text-slate-900 break-words">Verify Bank Account</h2>
                <p className="text-slate-600">Complete the verification process</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-4 sm:p-6 mb-6">
              <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Verification Required</h3>
                  <p className="text-slate-700 leading-relaxed break-words">
                    To complete the verification process, please enter the verification code that was sent to your email address. 
                    This helps us ensure the security of your account.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Verification Code *
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter verification code"
                  maxLength="8"
                />
              </div>

              <button
                type="submit"
                disabled={isVerifying || !verificationCode.trim()}
                className="btn-primary w-full sm:w-auto justify-center flex items-center px-8 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900"
              >
                {isVerifying ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Verifying...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Account
                  </>
                )}
              </button>
            </form>
          </div>
        )}


        <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-8 mt-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl typography-section-title font-bold text-slate-900 break-words">Payment Methods</h2>
                    <p className="text-slate-600 text-sm sm:text-base">Cards used for hotel and experience bookings</p>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                setIsStripeModalOpen(true)
                if (!stripeSetupClientSecret) await handleRequestStripeSetupIntent()
              }}
              disabled={isLoadingStripeCards || isLoadingCardSection}
              className="w-full sm:w-auto shrink-0 px-6 py-3 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
            >
              {isLoadingStripeCards || isLoadingCardSection ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                      'Add Card'
              )}
            </button>
          </div>
          {isLoadingStripeCards || isLoadingCardSection ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-12 h-12 border-4 border-slate-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : stripePaymentMethods.length > 0 ? (
            <div className="space-y-3">
              {stripePaymentMethods.map((pm) => (
                <div
                  key={pm._id || pm.id}
                  className="border border-slate-200 rounded-xl p-3 sm:p-4 hover:border-slate-300 transition-colors flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0"
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-6 flex items-center justify-center shrink-0">
                      {pm.walletType ? (
                        <Smartphone className="h-5 w-5 text-slate-600" />
                      ) : (
                        cardBrandIcons[pm.brand?.toLowerCase()] || cardBrandIcons.default
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 break-words flex flex-wrap items-center gap-x-2 gap-y-1">
                        {pm.walletType ? (
                          <>
                            <span>{pm.walletType === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}</span>
                            <span className="text-slate-400 font-normal">•••• {pm.last4}</span>
                          </>
                        ) : (
                          <span className="break-all sm:break-normal">{`•••• •••• •••• ${pm.last4}`}</span>
                        )}
                        {pm.isDefault && (
                          <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider shrink-0">Default</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 break-words mt-0.5">
                        {pm.walletType ? 'Digital Wallet' : pm.brand} • {pm.expiryMonth}/{pm.expiryYear}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteStripePaymentMethod(pm._id || pm.stripePaymentMethodId)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors self-end sm:self-center shrink-0"
                    title="Remove card"
                  >
                    <FaTimes className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
                    <p className="text-slate-500 text-sm py-4">No saved cards found. Add a card to use for bookings.</p>
          )}
        </div>

        {isStripeModalOpen && stripeSetupClientSecret && (
          <Elements 
            stripe={stripePromise} 
            options={{ 
              clientSecret: stripeSetupClientSecret,
              appearance: { theme: 'stripe' }
            }}
          >
            <AddStripeCardModal
              isOpen={isStripeModalOpen}
              onClose={() => { 
                setIsStripeModalOpen(false); 
                setStripeSetupClientSecret('');
                setStripeSetupCustomerSessionSecret('');
              }}
              onSubmit={handleSaveStripeCard}
              clientSecret={stripeSetupClientSecret}
            />
          </Elements>
        )}

        {isStripeModalOpen && !stripeSetupClientSecret && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl p-6 sm:p-8 flex flex-col items-center gap-4 max-w-[calc(100vw-2rem)]">
              <div className="w-12 h-12 border-4 border-slate-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-600">Preparing add card form...</p>
            </div>
          </div>
        )}

        {stripeDeleteModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/50" onClick={() => setStripeDeleteModalOpen(false)} />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[min(90dvh,32rem)] overflow-y-auto p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-slate-900">Remove Stripe Card</h3>
                </div>
                <p className="text-slate-600 mb-6">Remove this card from experience checkout? You can add it again later.</p>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setStripeDeleteModalOpen(false); setStripePaymentToDelete(null) }} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-700 font-medium">Cancel</button>
                  <button type="button" onClick={confirmDeleteStripePaymentMethod} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium">Remove</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
      </div>
    </MainLayout>
  )
}

export default BankAccountPage
